import React, { useContext, useEffect, useRef, useState } from 'react';
import { ELSButton } from '@els/els-component-button-react';
import { ELSInlineLoader } from '@els/els-ui-common-react';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import {
  GeneratedQuizQuestionDto,
  QuizQuestionSelectionDto
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { AppContext } from '../app/AppProvider.component';
import {
  createPracticeQuizAction,
  fetchGeneratedQuizQuestionsAction,
  fetchQuizQuestionsAction,
  trackNewRelicAction
} from '../app/app-state-actions.utilities';
import {
  Application,
  AppAction,
} from '../../apis/eols-app-link/eols-app-link.constants';
import { AiChatAssessmentPlayerOutBody } from '../../apis/eols-app-link/eols-app-link.dtos';
import { getExternalAppRedirectUrl } from '../../apis/eols-app-link/eols-app-link.utilities';
import { AppLinkOutConfig } from '../../apis/eols-app-link/eols-app-link.models';
import { getParentLinkId } from '../../utilities/app.utilities';
import { ServerConstants } from '../app/server.constants';
import { AssessmentPlayerIframeEvent } from '../../pages/ai-chat/ai-chat.constants';
import { getChatContextsForRequest, isAllowedMessageOrigin, toastApiError } from '../../pages/ai-chat/ai-chat.utilities';
import { FlexLayout } from '../flex/FlexLayout.component';
import { FlexItem } from '../flex/FlexItem.component';
import { FlexLayoutModifier } from '../flex/flex.constants';
import {
  NewRelicAnalyticsAction,
  QuizTakeFailureStage,
  QuizTakeSource
} from '../../models/new-relic-analytics.models';
import { copyContentToClipboard, formatQuizQuestionsForCopy } from '../../utilities/common.utilities';
import { BASE_TOAST_CONFIG } from '../../constants/toast.constants';
import IconWithText from '../icon-with-text/IconWithText.component';
import { ELSButtonIconPosition, ELSButtonSize, ELSButtonType } from '../../models/button.models';
import { ELSPaginator } from '../els.components';
import { ShuffleBar, ShuffleOrderButtons } from '../shuffle-bar/ShuffleBar.component';
import { useShuffledOrder } from '../shuffle-bar/useShuffledOrder.hook';
import {
  QUIZ_VIEW_PAGE_SIZE,
  QuizPanelMode
} from './quiz-questions-panel.constants';
import QuizQuestionList from './QuizQuestionList.component';

// Supply exactly one question source:
// - botEntryId: the in-chat quiz; fetches the entry's questions and takes its original assessment
// - selection: the Quiz Questions page's filters, sort and segment. It opens in the list's sort order at
//   once: VIEW fetches the whole selection and TAKE mints a new practice-quiz assessment over it for the
//   player. A button at the top then shuffles: VIEW reorders what it has fetched, while TAKE has to mint
//   a new assessment (the order is fixed when it is minted), which starts the quiz over.
export type QuizQuestionsPanelPropsOnly = {
  botEntryId?: number;
  selection?: QuizQuestionSelectionDto;
  // How many questions the selection held when it opened; a minted practice quiz has no question list to count
  questionCount?: number;
  // Shown at the right of the shuffle row, such as the page's close button, so both stay locked together
  headerEnd?: React.ReactNode;
  mode: QuizPanelMode;
  onComplete: () => void;
  // Called when the selection cannot be opened. The page toasts and closes the panel: a toast raised by the
  // panel's own toast service would be unmounted along with it.
  onError?: () => void;
};

export type QuizQuestionsPanelProps = QuizQuestionsPanelPropsOnly & ELSPropsFromToastService;

type QuizQuestionsPanelState = {
  // How many times TAKE was shuffled; each one mints a new shuffled assessment
  shuffleRun: number;
  // TAKE: a shuffled assessment is being minted while the current quiz stays on screen
  isShuffling: boolean;
  // As fetched; for a VIEW selection, in the list's sort order
  quizQuestions: GeneratedQuizQuestionDto[];
  quizPlayerUrl: string;
  assessmentId: number;
  isLoading: boolean;
  errorMessage: string;
  // 1-based, matching the paginator
  viewPage: number;
};

const defaultState: QuizQuestionsPanelState = {
  shuffleRun: 0,
  isShuffling: false,
  quizQuestions: null,
  quizPlayerUrl: null,
  assessmentId: null,
  isLoading: false,
  errorMessage: null,
  viewPage: 1,
};

export const QuizQuestionsPanelComponent = (props: QuizQuestionsPanelProps): JSX.Element => {

  const {
    appState,
    appStateRef,
    setAppState
  } = useContext(AppContext);

  const [state, setState] = useState<QuizQuestionsPanelState>(defaultState);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Wall-clock start of the current TAKE-mode setup attempt, so every outcome
  // event can report how long the user waited before it resolved.
  const takeStartTimeRef = useRef<number>(null);
  // Paginated VIEW: focus moves to the top of the list after a page change, not on first render.
  const viewListRef = useRef<HTMLDivElement>(null);
  const shouldFocusViewListRef = useRef(false);

  // Resolve the mode once so the fetch and render branches can never disagree
  // about what a missing mode means and leave the drawer blank.
  const mode = props.mode ?? QuizPanelMode.VIEW;
  const takeSource = props.botEntryId ? QuizTakeSource.CHAT : QuizTakeSource.QUIZ_QUESTIONS_PAGE;

  // Reshuffling a TAKE quiz mints a new assessment, so the current quiz stays up until it is ready
  const isReshuffle = !!props.selection && mode === QuizPanelMode.TAKE && state.shuffleRun > 0;

  // Pin displayOrder to list position: questions from different chats each restart their own
  // displayOrder, and QuizQuestionList sorts by it.
  const withListOrder = (questions: GeneratedQuizQuestionDto[]): GeneratedQuizQuestionDto[] => (
    questions.map((question, index) => ({ ...question, displayOrder: index }))
  );

  // VIEW reorders the questions it has fetched; TAKE never does, so this is always the fetched order there
  const viewOrder = useShuffledOrder(state.quizQuestions, withListOrder);
  const quizQuestions = viewOrder.items;

  const handleViewShuffle = () => {
    viewOrder.shuffle();
    setState((prevState) => ({ ...prevState, viewPage: 1 }));
  };

  const handleViewResetOrder = () => {
    viewOrder.reset();
    setState((prevState) => ({ ...prevState, viewPage: 1 }));
  };

  const handleTakeShuffle = () => {
    setState((prevState) => ({ ...prevState, shuffleRun: prevState.shuffleRun + 1 }));
  };

  const handleCopyClick = () => {
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_COPY_CLICK,
        props: {
          chatUserRole: appState.chatUserRole
        }
      }
    });

    const formattedQuestions = formatQuizQuestionsForCopy(quizQuestions);

    copyContentToClipboard(formattedQuestions);

    props.toastService.openToast({
      ...BASE_TOAST_CONFIG,
      component: (
        <div>Successfully copied all quiz questions</div>
      ),
      type: ELSWithToastService.types.POSITIVE
    });
  };

  const renderCopyFooter = (): JSX.Element => (
    <div className="c-naa-panel__quiz-footer u-els-margin-top">
      <FlexLayout modifiers={[
        FlexLayoutModifier.GUTTERS,
        FlexLayoutModifier.MIDDLE,
        FlexLayoutModifier.LEFT,
      ]}>
        <FlexItem>
          {/*
            The label is visible rather than screen-reader-only: users were
            missing the icon-only control. The visible text is the accessible
            name, so there is deliberately no aria-label to announce twice.
          */}
          <button
            onClick={handleCopyClick}
            type="button"
            className="u-els-debuttonize u-els-color-secondary u-els-display-block u-els-margin-top-1o8"
          >
            <IconWithText
              iconName="duplicate-document"
              iconPrefix="gizmo"
              iconPosition={ELSButtonIconPosition.LEFT}
            >
              Copy all questions
            </IconWithText>
          </button>
        </FlexItem>
      </FlexLayout>
    </div>
  );

  useEffect(() => {
    if (mode !== QuizPanelMode.TAKE) {
      return;
    }
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_TAKE_OPEN,
        props: {
          entryId: props.botEntryId,
          source: takeSource,
          chatUserRole: appState.chatUserRole,
        }
      }
    });
  }, [props.botEntryId, mode]);

  useEffect(() => {
    if (isReshuffle) {
      setState((prevState) => ({ ...prevState, isShuffling: true }));
    } else {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        quizQuestions: null,
        quizPlayerUrl: null,
        assessmentId: null,
        errorMessage: null,
        viewPage: 1,
      }));
    }

    const { nacsBaseUrl } = ServerConstants[ServerConstants.DataSource];

    takeStartTimeRef.current = Date.now();

    // Tracks which leg of the chain below is in flight so the shared catch can
    // attribute the failure. Both legs surface the same message to the user.
    let failureStage = QuizTakeFailureStage.FETCH_QUESTIONS;

    // A failed reshuffle leaves the quiz that was already running in place
    const failReshuffle = (error?: { message?: string; response?: { status?: number } }) => {
      trackNewRelicAction({
        appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_TAKE_ERROR,
          props: {
            entryId: props.botEntryId,
            source: takeSource,
            chatUserRole: appState.chatUserRole,
            stage: failureStage,
            errorMessage: error?.message || 'Failed to reshuffle the practice quiz',
            durationMs: Date.now() - takeStartTimeRef.current,
            ...(error?.response?.status !== undefined && error?.response?.status !== null
              ? { status: String(error.response.status) }
              : {}),
          }
        }
      });
      setState((prevState) => ({ ...prevState, isShuffling: false }));
      toastApiError(props.toastService);
    };

    const loadQuiz = (): Promise<{ questions: GeneratedQuizQuestionDto[]; assessmentId: number }> => {
      if (props.selection && mode === QuizPanelMode.TAKE) {
        failureStage = QuizTakeFailureStage.CREATE_PRACTICE_QUIZ;
        return createPracticeQuizAction({
          appState,
          appStateRef,
          setAppState,
          userRole: appState.chatUserRole,
          context: getChatContextsForRequest(appState),
          request: { ...props.selection, shuffle: state.shuffleRun > 0 },
        }).then((response) => ({ questions: null, assessmentId: response?.assessmentId }));
      }
      if (props.selection) {
        return fetchQuizQuestionsAction({
          appState,
          appStateRef,
          setAppState,
          userRole: appState.chatUserRole,
          context: getChatContextsForRequest(appState),
          params: { ...props.selection, unpaged: true },
        }).then((response) => {
          return { questions: withListOrder(response.content), assessmentId: null };
        });
      }
      return fetchGeneratedQuizQuestionsAction({
        appState,
        appStateRef,
        setAppState,
        botEntryId: props.botEntryId,
      }).then((questions) => ({ questions, assessmentId: questions?.[0]?.eolsAssessmentId }));
    };

    loadQuiz()
      .then(({ questions, assessmentId }) => {
        if (mode !== QuizPanelMode.TAKE) {
          setState((prevState) => ({
            ...prevState,
            quizQuestions: questions,
            isLoading: false,
            errorMessage: questions?.length ? null : 'No quiz questions found',
          }));
          return Promise.resolve();
        }

        if (!assessmentId) {
          if (isReshuffle) {
            failureStage = QuizTakeFailureStage.MISSING_ASSESSMENT_ID;
            failReshuffle();
            return Promise.resolve();
          }
          trackNewRelicAction({
            appStateRef,
            actionProps: {
              action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_TAKE_ERROR,
              props: {
                entryId: props.botEntryId,
                source: takeSource,
                chatUserRole: appState.chatUserRole,
                stage: QuizTakeFailureStage.MISSING_ASSESSMENT_ID,
                errorMessage: 'No eolsAssessmentId on the first generated quiz question',
                durationMs: Date.now() - takeStartTimeRef.current,
                questionCount: questions?.length || props.questionCount || 0,
              }
            }
          });
          setState((prevState) => ({
            ...prevState,
            isLoading: false,
            errorMessage: 'No quiz questions found',
          }));
          return Promise.resolve();
        }

        const outBody: AiChatAssessmentPlayerOutBody = {
          eolsUserId: Number(appState.userId),
          assessmentId,
          getNextQuestionAPI: `${nacsBaseUrl}/nacs/assessment/next-question?assessmentId=${assessmentId}`,
          submitQuestionAPI: `${nacsBaseUrl}/nacs/assessment/submit-question`,
          assessmentPlayerConfig: {
            navigationType: 'Submit-Continue',
            strictStudyMode: true,
            hideSubmissionConfirmationPage: true,
            showReportAtTheEndOfAssignment: false,
            hideSubmitModal: true,
            exitToSourceButton: false,
            notifyParentOnSessionEnd: true,
          },
          integrationMetadata: {
            name: 'Nora AI Quiz',
          },
          terminology: {
            assessmentEntityName: 'quiz',
          },
        };

        const config: AppLinkOutConfig = {
          action: AppAction.ASSESSMENT_START,
          app: Application.NHE_ASSESSMENT_PLAYER,
          body: outBody,
          altSrcApp: Application.NHE_AI,
          includeLinkHash: true,
          parentLinkId: getParentLinkId(appState.appLinkData, appState.appLinkCookies),
        };

        failureStage = QuizTakeFailureStage.APP_LINK_OUT;

        return getExternalAppRedirectUrl(config)
          .then((response) => {
            trackNewRelicAction({
              appStateRef,
              actionProps: {
                action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_TAKE_READY,
                props: {
                  entryId: props.botEntryId,
                  source: takeSource,
                  chatUserRole: appState.chatUserRole,
                  assessmentId,
                  questionCount: questions?.length || props.questionCount || 0,
                  durationMs: Date.now() - takeStartTimeRef.current,
                }
              }
            });
            setState((prevState) => ({
              ...prevState,
              quizQuestions: questions,
              quizPlayerUrl: response.redirectUrl,
              assessmentId,
              isLoading: false,
              isShuffling: false,
            }));
          });
      })
      .catch((error) => {
        if (isReshuffle) {
          failReshuffle(error);
          return;
        }
        const errorMessage = error?.response?.data?.message || 'Failed to load quiz questions';
        if (mode === QuizPanelMode.TAKE) {
          const responseStatus = error?.response?.status;
          trackNewRelicAction({
            appStateRef,
            actionProps: {
              action: NewRelicAnalyticsAction.AI_CHAT_QUIZ_TAKE_ERROR,
              props: {
                entryId: props.botEntryId,
                source: takeSource,
                chatUserRole: appState.chatUserRole,
                stage: failureStage,
                errorMessage: error?.message || errorMessage,
                durationMs: Date.now() - takeStartTimeRef.current,
                ...(responseStatus !== undefined && responseStatus !== null
                  ? { status: String(responseStatus) }
                  : {}),
              }
            }
          });
        }
        if (props.selection) {
          props.onError?.();
          return;
        }
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
          errorMessage,
        }));
      });
  }, [props.botEntryId, props.selection, state.shuffleRun, mode]);

  useEffect(() => {
    if (mode !== QuizPanelMode.TAKE || !props.onComplete) {
      return undefined;
    }

    const handleMessage = (e: MessageEvent) => {
      if (!isAllowedMessageOrigin(e.origin)) {
        return;
      }
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow) {
        return;
      }
      if (e.data?.type === AssessmentPlayerIframeEvent.ASSESSMENT_PLAYER_COMPLETE) {
        props.onComplete();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [props.onComplete, mode]);

  useEffect(() => {
    if (!shouldFocusViewListRef.current) {
      return;
    }
    shouldFocusViewListRef.current = false;
    // The list inside the wrapper is what scrolls, and React reuses it across pages, so start it at the top
    viewListRef.current?.querySelector('.c-naa-panel__quiz-list')?.scrollTo?.({ top: 0 });
    // Focusing scrolls the list's top into view and tells screen readers which questions loaded
    viewListRef.current?.focus();
  }, [state.viewPage]);

  // Only the Quiz Questions page's selection can be shuffled; a chat's quiz is fixed by its entry
  const renderShuffleBar = (): JSX.Element => {
    if (!props.selection) {
      return null;
    }
    return (
      <ShuffleBar headerEnd={props.headerEnd}>
        {mode === QuizPanelMode.VIEW ? (
          <ShuffleOrderButtons
            isShuffled={viewOrder.isShuffled}
            isDisabled={!state.quizQuestions?.length}
            onShuffle={handleViewShuffle}
            onReset={handleViewResetOrder}
          />
        ) : (
          <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE, FlexLayoutModifier.LEFT, FlexLayoutModifier.WRAP]}>
            <FlexItem>
              <ELSButton
                preventAutoPending
                type={ELSButtonType.SECONDARY}
                size={ELSButtonSize.SMALL}
                isDisabled={!state.quizPlayerUrl || state.isShuffling}
                onClick={handleTakeShuffle}
              >
                Shuffle and start over
              </ELSButton>
            </FlexItem>
            {state.isShuffling && (
              <FlexItem>
                <ELSInlineLoader />
              </FlexItem>
            )}
          </FlexLayout>
        )}
      </ShuffleBar>
    );
  };

  const renderContent = (): JSX.Element => {
    if (state.isLoading) {
      return (
        <div className="u-els-padding">
          <ELSInlineLoader />
          <div className="u-els-margin-top">Preparing your quiz questions… this may take a few seconds.</div>
        </div>
      );
    }

    if (state.errorMessage) {
      return (
        <div className="u-els-padding">
          <div className="u-els-color-warn">{state.errorMessage}</div>
        </div>
      );
    }

    if (mode === QuizPanelMode.VIEW && quizQuestions) {
      const isPaginated = quizQuestions.length > QUIZ_VIEW_PAGE_SIZE;
      const pageOffset = isPaginated ? (state.viewPage - 1) * QUIZ_VIEW_PAGE_SIZE : 0;
      // Sort the whole collection before slicing: QuizQuestionList only sorts within the page it is given.
      const pageQuestions = isPaginated
        ? [...quizQuestions]
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
            .slice(pageOffset, pageOffset + QUIZ_VIEW_PAGE_SIZE)
        : quizQuestions;
      return (
        <>
          {isPaginated ? (
            <div
              ref={viewListRef}
              className="c-naa-panel__quiz-list-group"
              tabIndex={-1}
              role="group"
              aria-label={`Quiz questions ${pageOffset + 1} to ${pageOffset + pageQuestions.length} of ${quizQuestions.length}`}
            >
              <QuizQuestionList questions={pageQuestions} questionNumberOffset={pageOffset} />
            </div>
          ) : (
            <QuizQuestionList questions={pageQuestions} questionNumberOffset={pageOffset} />
          )}
          {isPaginated && (
            <ELSPaginator
              id="quiz-questions-view-paginator"
              a11y={{
                name: 'Quiz questions pages',
                pagingUnitName: 'Page',
                morePagingUnit: 'More pages',
              }}
              currentPage={state.viewPage}
              totalResults={quizQuestions.length}
              resultsPerPage={QUIZ_VIEW_PAGE_SIZE}
              onPageChange={(viewPage: number) => {
                shouldFocusViewListRef.current = true;
                setState((prevState) => ({ ...prevState, viewPage }));
              }}
            />
          )}
          {renderCopyFooter()}
        </>
      );
    }

    if (state.quizPlayerUrl) {
      return (
        <>
          <iframe
            ref={iframeRef}
            src={state.quizPlayerUrl}
            className="c-naa-panel__quiz-iframe"
            title="Quiz Player"
            allowFullScreen
          />
          {/* A practice quiz minted from the Quiz Questions page has no question list to copy */}
          {!!quizQuestions?.length && renderCopyFooter()}
        </>
      );
    }

    return null;
  };

  // The shuffle controls and the close button stay put in every state, with only the questions below them scrolling
  if (props.selection) {
    return (
      <>
        {renderShuffleBar()}
        {renderContent()}
      </>
    );
  }

  return renderContent();
};

export const QuizQuestionsPanel: React.ComponentType<QuizQuestionsPanelPropsOnly> = ELSWithToastService(QuizQuestionsPanelComponent);

export default QuizQuestionsPanel;
